import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  Box,
  Container,
  Text,
  useColorModeValue,
  Flex,
  Spinner,
  IconButton,
  Button,
  Link as ChakraLink,
  Select,
  SimpleGrid,
  Grid,
  HStack,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
} from '@chakra-ui/react';
import { FiChevronLeft, FiChevronRight, FiCalendar } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import { useUserContext } from '../contexts/UserContext';
import { useMyLeagueStore } from '../store/myLeagueStore';
import { usePopulatedLeague } from '../hooks/usePopulatedLeague';
import { useLeagueSchedule } from '../hooks/useGames';
import TeamMultiSelect, { renderTeamChips } from '../components/TeamMultiSelect';
import TeamLogo from '../components/TeamLogo';
import PageTitle from '../components/PageTitle';
import ProjectedLineupModal, { type ProjectedLineupModalGame } from '../components/ProjectedLineupModal';
import { getTeamInfoById } from '../utils/getTeamInfo';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

function sortGamesMyTeamFirst<T extends { away_abbr: string; home_abbr: string }>(
  games: T[],
  myTeamId: number | null | undefined,
  abbrToId: Map<string, number>
): T[] {
  if (!myTeamId) return games;
  return [...games].sort((a, b) => {
    const aAway = abbrToId.get(a.away_abbr), aHome = abbrToId.get(a.home_abbr);
    const bAway = abbrToId.get(b.away_abbr), bHome = abbrToId.get(b.home_abbr);
    const aMine = aAway === myTeamId || aHome === myTeamId;
    const bMine = bAway === myTeamId || bHome === myTeamId;
    if (aMine && !bMine) return -1;
    if (!aMine && bMine) return 1;
    return 0;
  });
}

const LeagueSchedulePage: React.FC = () => {
  const { myTeam } = useUserContext();
  const myLeague = useMyLeagueStore((state) => state.myLeague);
  const leagueId = myLeague?.id;
  const { data: league, isLoading: leagueLoading, error } = usePopulatedLeague(leagueId);
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const headerBgColor = useColorModeValue('gray.50', 'gray.700');
  const gamePanelBg = useColorModeValue('gray.900', 'gray.800');

  const [searchParams] = useSearchParams();
  const dateParam = searchParams.get('date');
  const todayPST = dayjs().tz('America/Los_Angeles').startOf('day');
  const minDate = dayjs('2026-03-30').startOf('day');
  const today = todayPST.isBefore(minDate) ? minDate : todayPST;
  const defaultDate = today;
  const initialDateString = dateParam || defaultDate.format('YYYY-MM-DD');

  const [view, setView] = useState<'multi-day' | 'one-day' | 'month'>('one-day');
  const [displayDate, setDisplayDate] = useState(() => dayjs(initialDateString).startOf('day'));
  const [selectedDate, setSelectedDate] = useState(() => dayjs(initialDateString).startOf('day'));
  const [selectedTeamIds, setSelectedTeamIds] = useState<(string | number)[]>([]);
  const [selectedDayForModal, setSelectedDayForModal] = useState<dayjs.Dayjs | null>(null);
  const [projectedLineupGame, setProjectedLineupGame] = useState<ProjectedLineupModalGame | null>(null);
  const [isPotentialLineupOpen, setIsPotentialLineupOpen] = useState(false);
  const [isDayGamesModalOpen, setIsDayGamesModalOpen] = useState(false);
  const dateInputRef = useRef<HTMLInputElement>(null);
  const oneDayDateInputRef = useRef<HTMLInputElement>(null);

  const handleViewChange = (newView: 'multi-day' | 'one-day' | 'month') => {
    if (newView === 'one-day' && view !== 'one-day') {
      setSelectedDate(displayDate);
    } else if (newView === 'multi-day' && view === 'one-day') {
      setDisplayDate(selectedDate);
    } else if (newView === 'month' && view !== 'month') {
      if (view === 'one-day') {
        setDisplayDate(selectedDate);
      } else {
        setSelectedDate(displayDate);
      }
    }
    setView(newView);
  };

  const columnWidth = 200;
  const [columns, setColumns] = useState(() => {
    if (typeof window === 'undefined') return 5;
    const availableWidth = window.innerWidth - 200;
    return Math.min(5, Math.max(1, Math.floor(availableWidth / columnWidth)));
  });

  const startDate = displayDate.format('YYYY-MM-DD');
  const endDate = displayDate.add(columns - 1, 'day').format('YYYY-MM-DD');

  const { data: scheduleData, isLoading: isLoadingGames } = useLeagueSchedule({
    leagueId,
    startDate,
    endDate,
    enabled: !!leagueId && !!startDate && !!endDate,
  });

  const games = scheduleData?.games || [];

  const teamAbbrToIdMap = useMemo(() => {
    if (!league) return new Map<string, number>();
    const map = new Map<string, number>();
    league.conferences?.forEach((conf: { divisions?: { teams?: { id: number; abbreviation?: string; rw_abbreviation?: string }[] }[] }) => {
      conf.divisions?.forEach((div: { teams?: { id: number; abbreviation?: string; rw_abbreviation?: string }[] }) => {
        div.teams?.forEach((team: { id: number; abbreviation?: string; rw_abbreviation?: string }) => {
          const abbr = (team as { rw_abbreviation?: string }).rw_abbreviation || team.abbreviation;
          if (abbr) map.set(abbr, team.id);
          if (team.abbreviation && team.abbreviation !== (team as { rw_abbreviation?: string }).rw_abbreviation) {
            map.set(team.abbreviation, team.id);
          }
        });
      });
    });
    return map;
  }, [league]);

  const filteredGames = useMemo(() => {
    if (!selectedTeamIds?.length) return games;
    return games.filter((game: { home_abbr: string; away_abbr: string }) => {
      const homeTeamId = teamAbbrToIdMap.get(game.home_abbr);
      const awayTeamId = teamAbbrToIdMap.get(game.away_abbr);
      return (
        (homeTeamId && selectedTeamIds.includes(homeTeamId)) ||
        (awayTeamId && selectedTeamIds.includes(awayTeamId))
      );
    });
  }, [games, selectedTeamIds, teamAbbrToIdMap]);

  useEffect(() => {
    const handleResize = () => {
      if (typeof window === 'undefined') return;
      const availableWidth = window.innerWidth - 200;
      setColumns(Math.min(5, Math.max(1, Math.floor(availableWidth / columnWidth))));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const daysToShow = Array.from({ length: columns }, (_, i) => displayDate.add(i, 'day'));
  const handlePrev = () => setDisplayDate((prev) => prev.subtract(3, 'day'));
  const handleNext = () => setDisplayDate((prev) => prev.add(3, 'day'));
  const handleJumpToToday = () => setDisplayDate(today);
  const isTodayVisible = daysToShow.some((day) => day.isSame(today, 'day'));

  const oneDayStartDate = selectedDate.format('YYYY-MM-DD');
  const oneDayEndDate = selectedDate.format('YYYY-MM-DD');
  const { data: oneDayScheduleData, isLoading: isLoadingOneDayGames } = useLeagueSchedule({
    leagueId,
    startDate: oneDayStartDate,
    endDate: oneDayEndDate,
    enabled: view === 'one-day' && !!leagueId && !!oneDayStartDate && !!oneDayEndDate,
  });
  const oneDayGamesRaw = oneDayScheduleData?.games || [];

  const monthStart = displayDate.startOf('month');
  const monthEnd = displayDate.endOf('month');
  const monthStartDate = monthStart.format('YYYY-MM-DD');
  const monthEndDate = monthEnd.format('YYYY-MM-DD');
  const { data: monthScheduleData, isLoading: isLoadingMonthGames } = useLeagueSchedule({
    leagueId,
    startDate: monthStartDate,
    endDate: monthEndDate,
    enabled: view === 'month' && !!leagueId && !!monthStartDate && !!monthEndDate,
  });
  const monthGames = monthScheduleData?.games || [];

  const filteredMonthGames = useMemo(() => {
    if (!selectedTeamIds?.length) return monthGames;
    return monthGames.filter((game: { home_abbr: string; away_abbr: string }) => {
      const homeTeamId = teamAbbrToIdMap.get(game.home_abbr);
      const awayTeamId = teamAbbrToIdMap.get(game.away_abbr);
      return (
        (homeTeamId && selectedTeamIds.includes(homeTeamId)) ||
        (awayTeamId && selectedTeamIds.includes(awayTeamId))
      );
    });
  }, [monthGames, selectedTeamIds, teamAbbrToIdMap]);

  const oneDayGames = useMemo(() => {
    if (!selectedTeamIds?.length) return oneDayGamesRaw;
    return oneDayGamesRaw.filter((game: { home_abbr: string; away_abbr: string }) => {
      const homeTeamId = teamAbbrToIdMap.get(game.home_abbr);
      const awayTeamId = teamAbbrToIdMap.get(game.away_abbr);
      return (
        (homeTeamId && selectedTeamIds.includes(homeTeamId)) ||
        (awayTeamId && selectedTeamIds.includes(awayTeamId))
      );
    });
  }, [oneDayGamesRaw, selectedTeamIds, teamAbbrToIdMap]);

  const sortedOneDayGames = useMemo(
    () => sortGamesMyTeamFirst(oneDayGames, myTeam ?? null, teamAbbrToIdMap),
    [oneDayGames, myTeam, teamAbbrToIdMap]
  );

  const sortedFilteredMonthGames = useMemo(
    () => sortGamesMyTeamFirst(filteredMonthGames, myTeam ?? null, teamAbbrToIdMap),
    [filteredMonthGames, myTeam, teamAbbrToIdMap]
  );

  if (!myLeague) {
    return (
      <Container maxW="container.xl" py={8}>
        <PageTitle mb={4}>League Schedule</PageTitle>
        <Text color="gray.500" mb={4}>
          Select your league from the dashboard to view the schedule.
        </Text>
        <Button as={Link} to="/dashboard" colorScheme="blue">
          Go to Dashboard
        </Button>
      </Container>
    );
  }

  if (leagueLoading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Flex justify="center" align="center" minH="400px">
          <Spinner size="xl" />
        </Flex>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxW="container.xl" py={8}>
        <Text color="red.500">Error loading league</Text>
      </Container>
    );
  }

  if (!league) {
    return null;
  }

  const leagueName = league.name || myLeague.name;
  const scheduleTitle = `Schedule for the ${leagueName}${!leagueName.toLowerCase().includes('league') ? ' League' : ''}`;

  return (
    <Container maxW="container.xl" py={8}>
      <PageTitle mb={6}>{scheduleTitle}</PageTitle>

      {view === 'multi-day' && (
        <Box position="relative">
          {daysToShow.length > 0 && (
            <Box mb={4}>
              <Flex justify="space-between" align="center" flexWrap="wrap" gap={2} mb={2}>
                <HStack spacing={2}>
                  <Text fontSize="lg" fontWeight="medium">
                    {daysToShow[0].format('MMM D, YYYY')} to {daysToShow[daysToShow.length - 1].format('MMM D, YYYY')}
                  </Text>
                  <Box position="relative" display="inline-block">
                    <input
                      ref={dateInputRef}
                      type="date"
                      value={displayDate.format('YYYY-MM-DD')}
                      onChange={(e) => setDisplayDate(dayjs(e.target.value).startOf('day'))}
                      style={{
                        position: 'absolute',
                        inset: 0,
                        opacity: 0,
                        width: '100%',
                        height: '100%',
                        cursor: 'pointer',
                      }}
                      aria-label="Pick date"
                    />
                    <IconButton
                      aria-label="Pick date"
                      icon={<FiCalendar />}
                      size="sm"
                      variant="ghost"
                      pointerEvents="none"
                    />
                  </Box>
                </HStack>
                <HStack spacing={4}>
                  <Select
                    value={view}
                    onChange={(e) => handleViewChange(e.target.value as 'multi-day' | 'one-day' | 'month')}
                    width="200px"
                    size="sm"
                  >
                    <option value="one-day">One Day</option>
                    <option value="multi-day">Multi-Day</option>
                    <option value="month">Month</option>
                  </Select>
                  <TeamMultiSelect
                    league={league as Parameters<typeof TeamMultiSelect>[0]['league']}
                    selectedTeamIds={selectedTeamIds}
                    onChange={setSelectedTeamIds}
                    placeholder="Select Teams"
                  />
                </HStack>
              </Flex>
              {renderTeamChips(selectedTeamIds, league as Parameters<typeof renderTeamChips>[1], (id) =>
                setSelectedTeamIds(selectedTeamIds.filter((x) => x !== id))
              )}
            </Box>
          )}

          <IconButton
            aria-label="Previous 3 days"
            icon={<FiChevronLeft size={32} />}
            onClick={handlePrev}
            position="absolute"
            left="-30px"
            top="200px"
            zIndex={10}
            borderRadius="0 50% 50% 0"
            bg="rgba(255, 255, 255, 0.7)"
            color="gray.800"
            _hover={{ bg: 'rgba(255, 255, 255, 0.9)' }}
            size="lg"
          />
          {!isTodayVisible && daysToShow.length > 0 && (
            <Button onClick={handleJumpToToday} position="absolute" top="-40px" left="48px" size="sm" colorScheme="green" zIndex={10}>
              TODAY
            </Button>
          )}

          <Flex pl={12} pr={12} justifyContent="center" gap={4} overflowX="hidden">
            {daysToShow.map((day) => {
              const isToday = day.isSame(today, 'day');
              const gamesForDay = sortGamesMyTeamFirst(
                filteredGames.filter((g: { scheduled_date: string }) =>
                  dayjs(g.scheduled_date).isSame(day, 'day')
                ),
                myTeam ?? null,
                teamAbbrToIdMap
              );
              return (
                <Box
                  key={day.format('YYYY-MM-DD')}
                  width={`${columnWidth}px`}
                  minWidth={`${columnWidth}px`}
                  flexShrink={0}
                  borderWidth="2px"
                  borderColor={isToday ? 'green.700' : borderColor}
                  borderRadius="md"
                  bg={bgColor}
                >
                  <Box
                    px={4}
                    py={3}
                    borderBottomWidth="1px"
                    borderColor={isToday ? 'green.700' : borderColor}
                    bg={isToday ? 'green.700' : headerBgColor}
                  >
                    <Text fontWeight="bold" color={isToday ? 'white' : 'inherit'}>
                      {day.format('ddd, MMM D, YYYY')}
                    </Text>
                  </Box>
                  <Box px={4} py={3}>
                    {isLoadingGames ? (
                      <Flex justify="center" align="center" py={4}>
                        <Spinner size="sm" />
                      </Flex>
                    ) : gamesForDay.length > 0 ? (
                      gamesForDay.map((game: { away_abbr: string; home_abbr: string }, index: number) => (
                        <Text key={index} fontSize="sm" mb={1} lineHeight="1.8">
                          {game.away_abbr} at {game.home_abbr}
                        </Text>
                      ))
                    ) : (
                      <Text fontSize="sm" fontStyle="italic" color="gray.500">
                        No games
                      </Text>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Flex>

          <IconButton
            aria-label="Next 3 days"
            icon={<FiChevronRight size={32} />}
            onClick={handleNext}
            position="absolute"
            right="-30px"
            top="200px"
            zIndex={10}
            borderRadius="50% 0 0 50%"
            bg="rgba(255, 255, 255, 0.7)"
            color="gray.800"
            _hover={{ bg: 'rgba(255, 255, 255, 0.9)' }}
            size="lg"
          />
        </Box>
      )}

      {view === 'one-day' && (
        <Box>
          <Box mb={6}>
            <Flex justify="space-between" mb={2} align="center" flexWrap="wrap" gap={2}>
              <Flex align="center" gap={4}>
                <IconButton
                  aria-label="Previous day"
                  icon={<FiChevronLeft />}
                  onClick={() => setSelectedDate((prev) => prev.subtract(1, 'day'))}
                  size="sm"
                />
                <HStack spacing={2}>
                  <Text fontSize="lg" fontWeight="bold">
                    {selectedDate.format('ddd, MMM D, YYYY')}
                  </Text>
                  <Box position="relative" display="inline-flex" alignItems="center">
                    <input
                      ref={oneDayDateInputRef}
                      type="date"
                      value={selectedDate.format('YYYY-MM-DD')}
                      onChange={(e) => setSelectedDate(dayjs(e.target.value).startOf('day'))}
                      style={{
                        position: 'absolute',
                        inset: 0,
                        opacity: 0,
                        width: '100%',
                        height: '100%',
                        cursor: 'pointer',
                      }}
                      aria-label="Pick date"
                    />
                    <IconButton
                      aria-label="Pick date"
                      icon={<FiCalendar />}
                      size="sm"
                      variant="ghost"
                      pointerEvents="none"
                    />
                  </Box>
                </HStack>
                <IconButton
                  aria-label="Next day"
                  icon={<FiChevronRight />}
                  onClick={() => setSelectedDate((prev) => prev.add(1, 'day'))}
                  size="sm"
                />
                {!selectedDate.isSame(today, 'day') && (
                  <Button size="sm" colorScheme="green" onClick={() => setSelectedDate(today)}>
                    TODAY
                  </Button>
                )}
              </Flex>
              <HStack spacing={4}>
                <Select
                  value={view}
                  onChange={(e) => handleViewChange(e.target.value as 'multi-day' | 'one-day' | 'month')}
                  width="200px"
                  size="sm"
                >
                  <option value="one-day">One Day</option>
                  <option value="multi-day">Multi-Day</option>
                  <option value="month">Month</option>
                </Select>
                <TeamMultiSelect
                  league={league as Parameters<typeof TeamMultiSelect>[0]['league']}
                  selectedTeamIds={selectedTeamIds}
                  onChange={setSelectedTeamIds}
                  placeholder="Select Teams"
                />
              </HStack>
            </Flex>
            {renderTeamChips(selectedTeamIds, league as Parameters<typeof renderTeamChips>[1], (id) =>
              setSelectedTeamIds(selectedTeamIds.filter((x) => x !== id))
            )}
          </Box>

          {isLoadingOneDayGames ? (
            <Flex justify="center" align="center" minH="400px">
              <Spinner size="xl" />
            </Flex>
          ) : sortedOneDayGames.length === 0 ? (
            <Text textAlign="center" fontSize="lg" color="gray.500" py={8}>
              No games scheduled for this date
            </Text>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
              {sortedOneDayGames.map((game: Record<string, unknown>, index: number) => {
                const awayAbbr = game.away_abbr as string;
                const homeAbbr = game.home_abbr as string;
                const awayTeamId = teamAbbrToIdMap.get(awayAbbr);
                const homeTeamId = teamAbbrToIdMap.get(homeAbbr);
                const awayInfo = getTeamInfoById(awayTeamId ?? undefined, league as Parameters<typeof getTeamInfoById>[1]);
                const homeInfo = getTeamInfoById(homeTeamId ?? undefined, league as Parameters<typeof getTeamInfoById>[1]);
                const teamToLogo = (info: unknown, abbr: string) => {
                  if (info && typeof info === 'object' && 'name' in info) {
                    const t = info as { name: string; logo?: string; color_primary?: string; color_secondary?: string; color_text?: string };
                    return { name: t.name || abbr, abbreviation: abbr, logo: t.logo, color_primary: t.color_primary, color_secondary: t.color_secondary, color_text: t.color_text };
                  }
                  return { name: abbr, abbreviation: abbr };
                };
                const scheduledRaw = game.scheduled_date as string | undefined;
                return (
                  <Box
                    key={(game.id as number) || index}
                    bg={gamePanelBg}
                    borderRadius="4px"
                    borderWidth="1px"
                    borderColor="gray.700"
                    p={4}
                    boxShadow="sm"
                    display="flex"
                    flexDirection="column"
                    minH="100px"
                  >
                    <HStack spacing={4} align="center" fontWeight="bold" fontSize="lg" color="blue.200" flexWrap="nowrap">
                      <TeamLogo team={teamToLogo(awayInfo, awayAbbr)} size="sm" />
                      <Text as="span" lineHeight="1">{awayAbbr}</Text>
                      <Text as="span" fontWeight="normal" color="gray.400">at</Text>
                      <TeamLogo team={teamToLogo(homeInfo, homeAbbr)} size="sm" />
                      <Text as="span" lineHeight="1">{homeAbbr}</Text>
                      {game.series_game_number && game.series_length && (
                        <Text as="span" fontWeight="normal" fontSize="md" color="gray.400">
                          ({game.series_game_number} of {game.series_length})
                        </Text>
                      )}
                    </HStack>
                    <Flex mt="auto" pt={4} justify="space-between" align="center" w="100%" flexWrap="wrap" gap={2}>
                      <Text fontSize="sm" color="gray.400" textAlign="left">
                        {game.status !== 'scheduled' ? 'Completed' : 'Scheduled'}
                      </Text>
                      <ChakraLink
                        as="button"
                        type="button"
                        onClick={() => {
                          setProjectedLineupGame({
                            awayAbbr,
                            homeAbbr,
                            dateLabel: selectedDate.format('MMM D, YYYY'),
                            awayTeamId,
                            homeTeamId,
                            leagueId: typeof leagueId === 'number' ? leagueId : undefined,
                            gameDate: scheduledRaw
                              ? dayjs(scheduledRaw).format('YYYY-MM-DD')
                              : selectedDate.format('YYYY-MM-DD'),
                          });
                          setIsPotentialLineupOpen(true);
                        }}
                        color="green.300"
                        fontSize="sm"
                        fontWeight="medium"
                        textAlign="right"
                        _hover={{ color: 'green.200', textDecoration: 'underline' }}
                      >
                        Projected Lineups
                      </ChakraLink>
                    </Flex>
                  </Box>
                );
              })}
            </SimpleGrid>
          )}
        </Box>
      )}

      {view === 'month' && (() => {
        const otherMonthBg = useColorModeValue('gray.50', 'gray.900');
        const monthStartCal = displayDate.startOf('month');
        const firstDayOfMonth = monthStartCal.day();
        const daysInMonth = monthStartCal.daysInMonth();
        const calendarDays: dayjs.Dayjs[] = [];
        for (let i = firstDayOfMonth - 1; i >= 0; i--) {
          calendarDays.push(monthStartCal.subtract(i + 1, 'day'));
        }
        for (let i = 0; i < daysInMonth; i++) {
          calendarDays.push(monthStartCal.add(i, 'day'));
        }
        const remainingDays = 42 - calendarDays.length;
        for (let i = 1; i <= remainingDays; i++) {
          calendarDays.push(monthStartCal.endOf('month').add(i, 'day'));
        }
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

        return (
          <Box>
            <Flex justify="space-between" mb={6} align="center" flexWrap="wrap" gap={2}>
              <Flex align="center" gap={4}>
                <IconButton
                  aria-label="Previous month"
                  icon={<FiChevronLeft />}
                  onClick={() => setDisplayDate((prev) => prev.subtract(1, 'month'))}
                  size="sm"
                />
                <Text fontSize="xl" fontWeight="bold">
                  {displayDate.format('MMMM YYYY')}
                </Text>
                <IconButton
                  aria-label="Next month"
                  icon={<FiChevronRight />}
                  onClick={() => setDisplayDate((prev) => prev.add(1, 'month'))}
                  size="sm"
                />
                {!displayDate.isSame(today, 'month') && (
                  <Button size="sm" colorScheme="green" onClick={() => setDisplayDate(today)}>
                    TODAY
                  </Button>
                )}
              </Flex>
              <HStack spacing={4}>
                <Select
                  value={view}
                  onChange={(e) => handleViewChange(e.target.value as 'multi-day' | 'one-day' | 'month')}
                  width="200px"
                  size="sm"
                >
                  <option value="one-day">One Day</option>
                  <option value="multi-day">Multi-Day</option>
                  <option value="month">Month</option>
                </Select>
                <TeamMultiSelect
                  league={league as Parameters<typeof TeamMultiSelect>[0]['league']}
                  selectedTeamIds={selectedTeamIds}
                  onChange={setSelectedTeamIds}
                  placeholder="Select Teams"
                />
              </HStack>
            </Flex>
            {renderTeamChips(selectedTeamIds, league as Parameters<typeof renderTeamChips>[1], (id) =>
              setSelectedTeamIds(selectedTeamIds.filter((x) => x !== id))
            )}

            {isLoadingMonthGames ? (
              <Flex justify="center" align="center" minH="400px">
                <Spinner size="xl" />
              </Flex>
            ) : (
              <>
                <Grid templateColumns="repeat(7, 1fr)" gap={1} mb={2}>
                  {dayNames.map((d) => (
                    <Box key={d} py={2} textAlign="center">
                      <Text fontWeight="bold" fontSize="sm" color="gray.600">
                        {d}
                      </Text>
                    </Box>
                  ))}
                </Grid>
                <Grid templateColumns="repeat(7, 1fr)" gap={1}>
                  {calendarDays.map((day, index) => {
                    const isCurrentMonth = day.month() === displayDate.month();
                    const isToday = day.isSame(today, 'day');
                    const gamesForDay = sortedFilteredMonthGames.filter((g: { scheduled_date: string }) =>
                      dayjs(g.scheduled_date).isSame(day, 'day')
                    );
                    return (
                      <Box
                        key={index}
                        minH="120px"
                        borderWidth={isToday ? '2px' : '1px'}
                        borderColor={isToday ? 'green.700' : borderColor}
                        borderRadius="md"
                        bg={isCurrentMonth ? bgColor : otherMonthBg}
                        p={2}
                        cursor={isCurrentMonth ? 'pointer' : 'default'}
                        _hover={isCurrentMonth ? { borderColor: 'blue.500', borderWidth: '2px', boxShadow: 'md' } : {}}
                        onClick={() => {
                          if (isCurrentMonth) {
                            setSelectedDayForModal(day);
                            setIsDayGamesModalOpen(true);
                          }
                        }}
                      >
                        <Text
                          fontSize="sm"
                          fontWeight={isToday ? 'bold' : 'normal'}
                          color={isCurrentMonth ? (isToday ? 'green.700' : 'inherit') : 'gray.400'}
                          mb={1}
                        >
                          {day.date()}
                        </Text>
                        <Box overflowY="auto" maxH="90px">
                          {gamesForDay.length > 0 ? (
                            gamesForDay.slice(0, 3).map((g: { away_abbr: string; home_abbr: string }, gi: number) => (
                              <Text key={gi} fontSize="xs" color={isCurrentMonth ? 'inherit' : 'gray.400'} mb={0.5} noOfLines={1}>
                                {g.away_abbr} @ {g.home_abbr}
                              </Text>
                            ))
                          ) : (
                            isCurrentMonth && (
                              <Text fontSize="xs" color="gray.400" fontStyle="italic">
                                No games
                              </Text>
                            )
                          )}
                          {gamesForDay.length > 3 && (
                            <Text fontSize="xs" color="blue.500" fontWeight="bold">
                              +{gamesForDay.length - 3} more
                            </Text>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Grid>
              </>
            )}
          </Box>
        );
      })()}

      {isPotentialLineupOpen && (
        <ProjectedLineupModal
          isOpen={isPotentialLineupOpen}
          onClose={() => {
            setProjectedLineupGame(null);
            setIsPotentialLineupOpen(false);
          }}
          game={projectedLineupGame}
          league={league}
        />
      )}

      {isDayGamesModalOpen && (
      <Modal
        isOpen={isDayGamesModalOpen}
        onClose={() => setIsDayGamesModalOpen(false)}
        size="xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {selectedDayForModal ? selectedDayForModal.format('dddd, MMMM D, YYYY') : 'Schedule'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedDayForModal && (() => {
              const dayGames = sortedFilteredMonthGames.filter((g: { scheduled_date: string }) =>
                dayjs(g.scheduled_date).isSame(selectedDayForModal, 'day')
              );
              if (dayGames.length === 0) {
                return (
                  <Text textAlign="center" fontSize="lg" color="gray.500" py={8}>
                    No games scheduled for this date
                  </Text>
                );
              }
              return (
                <SimpleGrid columns={1} spacing={4}>
                  {dayGames.map((g: { away_abbr: string; home_abbr: string; id?: number }, i: number) => (
                    <Box key={i} bg={gamePanelBg} borderRadius="4px" borderWidth="1px" borderColor="gray.700" p={4} boxShadow="sm">
                      <Text fontWeight="bold" fontSize="lg" color="blue.200" noOfLines={1}>
                        {g.away_abbr} at {g.home_abbr}
                      </Text>
                    </Box>
                  ))}
                </SimpleGrid>
              );
            })()}
          </ModalBody>
        </ModalContent>
      </Modal>
      )}
    </Container>
  );
};

export default LeagueSchedulePage;
